/**
 * Phase 8 · C-49 · Shared viz — ProgressChip.
 *
 * Pill with label + value/target + a thin bar fill underneath.
 * Tone controls the fill colour (success | warning | neutral).
 *
 * Uses CSS variables from @shared/components/viz/tokens.css — hand-rolled,
 * no chart lib.
 */
import './tokens.css';

export type ProgressChipTone = 'neutral' | 'success' | 'warning';

export interface ProgressChipProps {
  value: number;
  target: number;
  label: string;
  tone?: ProgressChipTone;
  /** Optional testid for reliable e2e selection. */
  testId?: string;
}

const TONE_VAR: Record<ProgressChipTone, string> = {
  neutral: 'var(--viz-neutral)',
  success: 'var(--viz-success)',
  warning: 'var(--viz-warning)',
};

export function ProgressChip({ value, target, label, tone = 'neutral', testId }: ProgressChipProps) {
  const safeTarget = target > 0 ? target : 1;
  const pct = Math.max(0, Math.min(1, value / safeTarget));
  const fillColor = TONE_VAR[tone];

  return (
    <div
      data-testid={testId}
      className="inline-flex min-w-[120px] flex-col gap-1 rounded-lg border px-3 py-2"
      style={{ borderColor: 'var(--viz-border)', background: 'var(--viz-surface)' }}
      role="group"
      aria-label={`${label}: ${value} of ${target}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wide" style={{ color: 'var(--viz-text-muted)' }}>
          {label}
        </span>
        <span className="text-[13px] font-semibold tabular-nums" style={{ color: 'var(--viz-text)' }}>
          {value}
          <span style={{ color: 'var(--viz-text-muted)' }}>/{target}</span>
        </span>
      </div>
      <div
        className="h-1 overflow-hidden rounded-full"
        style={{ background: 'var(--viz-track)' }}
        aria-hidden
      >
        <div
          className="h-full rounded-full transition-[width] duration-300"
          style={{ width: `${pct * 100}%`, background: fillColor }}
        />
      </div>
    </div>
  );
}
